export interface ServiceConfig {
    authService?: string;
    signalDb?: string;
    literatureService?: string;
    aiService?: string;
    safetyDb?: string;
    caseDb?: string;
    regulatoryGatewayService?: string;
}

let cache: ServiceConfig | null = null;
let pendingPromise: Promise<ServiceConfig> | null = null;

/**
 * A PROD (nginx) build serves same-origin paths such as "/case-db". The shared axios
 * instance puts safetyDb in baseURL, and axios glues a path-only url onto that base
 * ("/sdb-service/api/v1/case-db/..."), so each path becomes an address on this origin.
 */
const withOrigin = (config: ServiceConfig): ServiceConfig =>
    Object.fromEntries(Object.entries(config).map(([key, url]) =>
        [key, typeof url === 'string' && url.startsWith('/') && !url.startsWith('//')
            ? `${window.location.origin}${url}` : url]));

export const getServiceConfig = async (): Promise<ServiceConfig> => {
    if (cache) return cache;

    // Return existing promise if a fetch is already in progress
    if (pendingPromise) return pendingPromise;

    pendingPromise = (async () => {
        try {
            // We use relative path so it hits the Next.js server of the same origin
            const res = await fetch('/api/runtime-services', {
                // cache: 'force-cache', // Use browser cache if available for short duration? 
                // user showed 'no-store' in previous turns. 
                // Let's stick to preventing race conditions first.
                // The user asked to "cache it". 
                // We'll trust the in-memory cache for the session.
                cache: 'no-store'
            });

            if (!res.ok) {
                console.warn(`Failed to fetch service config: ${res.status} ${res.statusText}`);
                return {};
            }

            cache = withOrigin(await res.json());
        } catch (error) {
            console.error('Error loading service config:', error);
            // Return empty object to allow fallback to defaults/baseURL
            return {};
        } finally {
            pendingPromise = null;
        }

        return cache || {};
    })();

    return pendingPromise;
};

/**
 * synchronous helper to get cache if already loaded, 
 * useful if we can't await (though in axios interceptors we can await)
 */
export const getCachedServiceConfig = (): ServiceConfig | null => {
    return cache;
};
